/**
 * API publique `window.Smootify` — reproduite à l'identique Smootify.
 * Réf : https://docs.smootify.io/docs/custom-code/js/api
 *
 * 12 méthodes. Le contrat doit matcher exactement pour le custom code LMDR
 * existant qui écoute ces méthodes.
 */

import { storefront } from "../storefront/client.js";
import {
  addToCart as cartAddToCart,
  clearCart as cartClear,
  applyCouponCode as cartApplyCoupon,
  reloadCart as cartReload,
  getCartID as cartGetId,
} from "../cart/state.js";
import {
  setCountry,
  setLanguage,
} from "../i18n/locale.js";
import { formatMoney as uiFormatMoney } from "../ui/format.js";
import type { Cart, CartLineInput } from "../types.js";

// -----------------------------------------------------------------------------
//  Types publics
// -----------------------------------------------------------------------------
export interface SmootifyApi {
  query<T = unknown>(queryString: string, variables?: Record<string, unknown>): Promise<T>;
  queryCustomer<T = { customer: unknown }>(fields: string): Promise<T>;
  setCustomerMetafields(...metafields: Array<{ key: string; value: string; namespace?: string; type?: string }>): Promise<void>;
  applyCouponCode(code: string): Promise<void>;
  changeCountryByIsoCode(iso: string): Promise<void>;
  changeMarketLanguage(iso: string): Promise<void>;
  addToCart(lines: CartLineInput[], goToCheckout?: boolean): Promise<Cart>;
  getCartID(): Promise<string | null>;
  reloadCart(): Promise<void>;
  clearCart(): Promise<void>;
  formatMoney(value: string | number | { amount: string | number; currencyCode?: string }): string;
  addBoxToCart(
    lines: CartLineInput[],
    boxOptions?: { title?: string; image?: string },
    goToCheckout?: boolean,
  ): Promise<Cart>;
}

// -----------------------------------------------------------------------------
//  Implémentation
// -----------------------------------------------------------------------------
export function createSmootifyApi(): SmootifyApi {
  return {
    async query(queryString, variables) {
      return storefront.query(queryString, variables ?? {});
    },

    async queryCustomer(fields) {
      // Customer Account API (OAuth) : TODO en v2 accounts.
      // Pour l'instant on throw pour signaler que le user n'est pas loggé
      // — Smootify throw également dans ce cas.
      throw new Error("Customer not logged in (Customer Accounts API not yet implemented)");
    },

    async setCustomerMetafields(..._metafields) {
      throw new Error("setCustomerMetafields requires customer login (v2)");
    },

    async applyCouponCode(code) {
      await cartApplyCoupon(code);
    },

    async changeCountryByIsoCode(iso) {
      await setCountry(iso);
    },

    async changeMarketLanguage(iso) {
      await setLanguage(iso);
    },

    async addToCart(lines, goToCheckout) {
      return cartAddToCart(lines, goToCheckout === true);
    },

    async getCartID() {
      return cartGetId();
    },

    async reloadCart() {
      await cartReload();
    },

    async clearCart() {
      await cartClear();
    },

    formatMoney(value) {
      // Smootify.formatMoney accepte 3 formes :
      //   - MoneyV2 {amount, currencyCode}      → format directement
      //   - number/string entier ≥ 100 ("cents") → divise par 100
      //   - number/string décimal ("1.99" ou 1.99) → valeur en unités
      if (value !== null && typeof value === "object" && "amount" in value) {
        return uiFormatMoney({
          amount: String(value.amount),
          currencyCode: value.currencyCode ?? "EUR",
        });
      }
      const n = typeof value === "string" ? parseFloat(value) : Number(value);
      if (!Number.isFinite(n)) return uiFormatMoney({ amount: "0", currencyCode: "EUR" });
      // Heuristique : si c'est un entier ≥ 100, on assume que c'est des cents.
      // Sinon (décimal ou petit entier), on prend la valeur brute.
      const asCents = Number.isInteger(n) && Math.abs(n) >= 100;
      const amount = asCents ? n / 100 : n;
      return uiFormatMoney({ amount: String(amount), currencyCode: "EUR" });
    },

    async addBoxToCart(lines, _boxOptions, goToCheckout) {
      // Magic Box add-on non utilisé sur LMDR — fallback sur addToCart standard.
      return cartAddToCart(lines, goToCheckout === true);
    },
  };
}

/**
 * Installe l'API sur `window.Smootify`. Idempotent.
 * Expose aussi `window.smootify` (lowercase) pour compat.
 */
export function installSmootifyApi(): SmootifyApi {
  const api = createSmootifyApi();
  const w = window as unknown as {
    Smootify?: SmootifyApi;
    smootify?: SmootifyApi;
  };
  w.Smootify = api;
  w.smootify = api;
  return api;
}
